import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

import { base, errorCodeOf, readBulkResult, unwrap, withParty } from '@/app/cancellations/engine/api-shapes';
import {
	type CancellationReason,
	type IBulkCancellationResult,
	type ICancellationAlertSettings,
	type ICancellationRow,
} from '@/app/cancellations/types';
import { httpStatusOf } from '@/app/live-election/engine/capability';
import { liveElectionKeys } from '@/hooks/queries/live-election/keys';
import { myElectionKeys } from '@/hooks/queries/my-election/my-election-keys';
import { coverageTimelineKeys } from '@/hooks/queries/result-time-machine/keys';
import { strongHoldKeys } from '@/hooks/queries/strong-hold/keys';
import { coreApi } from '@/lib/api-client';

import { cancellationKeys } from './keys';

/**
 * Everything a cancellation can change: the ledger and its impact, the live-election
 * summary, the Strong Hold estimate (a cancelled unit's expected votes are lost), and the
 * coverage reads whose denominators exclude cancelled units.
 */
export const invalidateAfterCancellationChange = (queryClient: QueryClient, electionId: number, partyKey: number) => {
	void queryClient.invalidateQueries({ queryKey: cancellationKeys.election(electionId) });
	void queryClient.invalidateQueries({ queryKey: liveElectionKeys.summaries(electionId) });
	void queryClient.invalidateQueries({ queryKey: strongHoldKeys.estimates(electionId, partyKey) });
	void queryClient.invalidateQueries({ queryKey: coverageTimelineKeys.all });
	void queryClient.invalidateQueries({ queryKey: myElectionKeys.all });
};

export type RowMutationOutcome =
	| { kind: 'done'; row: ICancellationRow | null }
	| { kind: 'notActive' }
	| { kind: 'forbidden' }
	| { kind: 'error'; message: string };

export const toOutcome = (error: unknown): RowMutationOutcome => {
	const status = httpStatusOf(error);
	const code = errorCodeOf(error);
	if (status === 409 && code === 'RACE_LOST') {
		return { kind: 'error', message: 'Someone else changed this unit at the same moment. Try again.' };
	}
	if (status === 409) return { kind: 'notActive' };
	if (status === 403) return { kind: 'forbidden' };
	return {
		kind: 'error',
		message: status === 429 ? 'Too many requests. Wait a moment and try again.' : 'That did not save. Try again.',
	};
};

/**
 * A 409 `RACE_LOST` means the row's concurrency token moved under us (another admin, or the
 * agent's withdraw) — the documented answer is "resend", once, before telling anyone.
 */
export const withRaceRetry = async <T>(send: () => Promise<T>): Promise<T> => {
	try {
		return await send();
	} catch (error) {
		if (httpStatusOf(error) === 409 && errorCodeOf(error) === 'RACE_LOST') return send();
		throw error;
	}
};

const rowOf = (body: unknown): ICancellationRow | null => {
	const data = unwrap<{ row?: ICancellationRow } | ICancellationRow>(body);
	if (!data || typeof data !== 'object') return null;
	return 'row' in data && data.row ? data.row : (data as ICancellationRow);
};

interface IScopeArgs {
	electionId: number;
	partyId: number | null;
	partyKey: number;
}

/** Confirm an agent's report. Idempotent server-side. */
export function useConfirmCancellation({ electionId, partyId, partyKey }: IScopeArgs) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: number): Promise<RowMutationOutcome> => {
			try {
				const { data } = await withRaceRetry(() => coreApi.post(withParty(`${base(electionId)}/${id}/review`, partyId)));
				return { kind: 'done', row: rowOf(data) };
			} catch (error) {
				return toOutcome(error);
			}
		},
		onSettled: () => invalidateAfterCancellationChange(queryClient, electionId, partyKey),
	});
}

/**
 * Reinstate (soft). A v2 server takes a reason; a v1 server only has DELETE, which the v2
 * contract keeps as an alias — so the fallback still records the action, just without the
 * reason, and the dialog says so.
 */
export function useReinstateCancellation({ electionId, partyId, partyKey }: IScopeArgs) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (args: { id: number; reason: string; v2: boolean }): Promise<RowMutationOutcome> => {
			try {
				if (args.v2) {
					const { data } = await withRaceRetry(() =>
						coreApi.post(withParty(`${base(electionId)}/${args.id}/reinstate`, partyId), {
							reason: args.reason.trim().slice(0, 500),
						})
					);
					return { kind: 'done', row: rowOf(data) };
				}
				await withRaceRetry(() => coreApi.delete(withParty(`${base(electionId)}/${args.id}`, partyId)));
				return { kind: 'done', row: null };
			} catch (error) {
				return toOutcome(error);
			}
		},
		onSettled: () => invalidateAfterCancellationChange(queryClient, electionId, partyKey),
	});
}

export type BulkChunkOutcome =
	| { kind: 'recorded'; result: IBulkCancellationResult }
	/** 400: nothing in the chunk was written; the body names the offending codes. */
	| { kind: 'rejected'; result: IBulkCancellationResult; message: string }
	| { kind: 'error'; message: string };

export interface IBulkChunkInput {
	codes: string[];
	reason: CancellationReason;
	notes?: string;
	cancelledAt?: string;
}

/**
 * POST one ≤500-code chunk. A 409 (concurrent write) is resent once automatically — the
 * documented meaning is "resend", and an admin pasting INEC's list should not have to.
 */
export const postBulkChunk = async (
	electionId: number,
	partyId: number | null,
	input: IBulkChunkInput,
	attempt = 0
): Promise<BulkChunkOutcome> => {
	try {
		const { data } = await coreApi.post(withParty(base(electionId), partyId), {
			pollingUnitCodes: input.codes,
			reason: input.reason,
			notes: input.notes?.trim() || undefined,
			cancelledAt: input.cancelledAt || undefined,
		});
		return { kind: 'recorded', result: readBulkResult(data) };
	} catch (error) {
		const status = httpStatusOf(error);
		if (status === 409 && attempt === 0) return postBulkChunk(electionId, partyId, input, 1);
		if (status === 400) {
			const body = (error as { response?: { data?: unknown } }).response?.data;
			const message = (body as { message?: string })?.message || 'Some codes were not accepted.';
			return { kind: 'rejected', result: readBulkResult(body), message };
		}
		if (status === 409) return { kind: 'error', message: 'Someone else changed the ledger at the same moment. Send again.' };
		if (status === 403) return { kind: 'error', message: 'You cannot record cancellations for this party.' };
		if (status === 429) return { kind: 'error', message: 'Too many requests. Wait a moment and send again.' };
		return { kind: 'error', message: 'The server did not accept this batch. Send again.' };
	}
};

export function useSaveAlertSettings({ electionId, partyId, partyKey }: IScopeArgs) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (settings: ICancellationAlertSettings) => {
			const { data } = await coreApi.put(withParty(`${base(electionId)}/alert-settings`, partyId), settings);
			return unwrap<ICancellationAlertSettings>(data);
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: cancellationKeys.alertSettings(electionId, partyKey) });
		},
	});
}
